/* eslint-disable */
import type { Ticker } from 'pixi.js';
// import console from './logger';

export interface IFuturePromise<T = void> {
  isResolved: () => boolean;
  resolve: (resolveValue: T) => void;
  reject: (error?: unknown) => void;
  promise: Promise<T>;
}

export function createFuturePromise<T = void>(): IFuturePromise<T> {
  let resolveFunc: (resolveValue: T) => void;
  let rejectFunc: (error?: unknown) => void;

  let resolved = false;

  const promise = new Promise<T>((resolve: (resolveValue: T) => void, reject: (error: unknown) => void): void => {
    resolveFunc = resolve;
    rejectFunc = reject;
  });

  promise.then(() => {
    resolved = true;
  });

  return {
    isResolved: () => resolved,
    resolve: resolveFunc!,
    reject: rejectFunc!,
    promise,
  };
}

export const waitForMs = (time: number) =>
  new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, time);
  });

export async function waitForNextFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      resolve();
    });
  });
}

export function debounce(func: (...args: unknown[]) => void, wait: number, immediate = false) {
  let timeout: number | null;

  return function executedFunction(this: unknown, ...args: unknown[]) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const context = this;

    const later = () => {
      timeout = null;

      if (!immediate) {
        func.call(context, ...args);
      }
    };

    const callNow = immediate && !timeout;
    clearTimeout(timeout!);
    timeout = setTimeout(later, wait) as unknown as number;

    if (callNow) {
      func.call(context, ...args);
    }
  };
}

export function timeOutFallback<T>(promiseA: Promise<T>, fallback: T, timeOut: number): Promise<T> {
  return new Promise((resolve) => {
    promiseA.then((result) => resolve(result));
    waitForMs(timeOut).then(() => resolve(fallback));
  });
}

export async function repeatUntilSuccessful<T>(func: () => T | Promise<T>, delayBetweenRetries = 100): Promise<T> {
  while (true) {
    try {
      return await func();
    } catch (e) {
      console.warn(e);
      await waitForMs(delayBetweenRetries);
    }
  }
}

export async function repeatAsync(iterations: number, callback: (iteration: number) => Promise<void>): Promise<void> {
  for (let i = 0; i < iterations; i += 1) {
    await callback(i);
  }
}

export async function repeatUntilFalse(func: (index: number) => boolean, delayBetweenRetries = 100): Promise<void> {
  let i = 0;
  while (true) {
    if (!func(i++)) return;
    await waitForMs(delayBetweenRetries);
  }
}

export async function repeatAsyncUntilFalse(func: (index: number) => Promise<boolean>, delayBetweenRetries = 100): Promise<void> {
  let i = 0;
  while (true) {
    if (!(await func(i++))) return;
    await waitForMs(delayBetweenRetries);
  }
}

export async function handleOfflinePromise<T>(
  promiseGenerator: () => Promise<T>,
  timeout: number | null = null,
): Promise<T | void> {
  if (!window.navigator.onLine) {
    return Promise.reject('navigator offline');
  }

  if (timeout === null) {
    return promiseGenerator();
  }

  const timeoutPromise: Promise<void> = new Promise((_, reject) => {
    setTimeout(() => {
      reject('promise timeout');
    }, timeout);
  });

  return Promise.race([timeoutPromise, promiseGenerator()]);
}

export function timeOutValue<T, K>(promise: Promise<T>, timeOut: number, timeOutValue: K): Promise<T | K> {
  return Promise.race([promise, waitForMs(timeOut).then(() => timeOutValue)]);
}

export function setTickerBasedTimeout(ticker: Ticker, func: () => unknown, delayMs: number): () => void {
  let elapsedMs = 0;
  const tick = () => {
    elapsedMs += ticker.elapsedMS;
    if (elapsedMs >= delayMs) {
      func();
      ticker.remove(tick);
    }
  };

  ticker.add(tick);

  return () => {
    ticker.remove(tick);
  };
}
